import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HierarchyNodeBadge from './HierarchyNodeBadge';
import { formatMentorName } from './hierarchyUtils';

/**
 * Indented, collapsible org chart. The default view — reliable at any org
 * size and the one that pairs well with search.
 */
const TreeRow = ({ node, depth, onEdit, collapsed, onToggle, matchesSearch }) => {
  const hasChildren = node.children?.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const dimmed = matchesSearch && !matchesSearch(node);

  return (
    <>
      <div
        className={`hierarchy-row${dimmed ? ' hierarchy-row-dimmed' : ''}`}
        // Indentation is capped: past ~8 levels the nesting is still legible
        // from the caret column, and uncapped padding pushes the name and
        // controls off narrow screens entirely.
        style={{ paddingLeft: `${Math.min(depth, 8) * 1.5}rem` }}
      >
        <button
          type="button"
          className="hierarchy-toggle"
          onClick={() => hasChildren && onToggle(node.id)}
          aria-label={hasChildren ? (isCollapsed ? 'Expand' : 'Collapse') : undefined}
          disabled={!hasChildren}
        >
          {hasChildren ? <i className={`bi ${isCollapsed ? 'bi-caret-right-fill' : 'bi-caret-down-fill'}`} /> : <span className="hierarchy-toggle-spacer" />}
        </button>

        <span className="hierarchy-row-name">{formatMentorName(node)}</span>
        <HierarchyNodeBadge hierarchy={node.hierarchy} />

        {node.manuallyAssigned && (
          <i
            className="bi bi-lock-fill hierarchy-lock"
            title="Set manually — the Testpress sync will not change this mentor's hierarchy"
          />
        )}

        {node.rankViolation && (
          <i
            className="bi bi-exclamation-triangle-fill hierarchy-violation"
            title="This mentor's current supervisor does not outrank them. Edit to fix."
          />
        )}

        {node.inCycle && (
          <i
            className="bi bi-arrow-repeat hierarchy-violation"
            title="This mentor is part of a supervision loop. Clear one of the links to break it."
          />
        )}

        <span className="hierarchy-row-meta">
          {node.studentsCount || 0} student{node.studentsCount === 1 ? '' : 's'}
          {hasChildren ? ` · ${node.children.length} report${node.children.length === 1 ? '' : 's'}` : ''}
        </span>

        <Link
          to={`/analytics?mentor=${node.id}`}
          className="hierarchy-edit-btn"
          title="View this mentor's app usage analytics"
        >
          <i className="bi bi-graph-up" /> Usage
        </Link>

        <button type="button" className="hierarchy-edit-btn" onClick={() => onEdit(node)}>
          <i className="bi bi-pencil" /> Edit
        </button>
      </div>

      {hasChildren && !isCollapsed &&
        node.children.map((child) => (
          <TreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            onEdit={onEdit}
            collapsed={collapsed}
            onToggle={onToggle}
            matchesSearch={matchesSearch}
          />
        ))}
    </>
  );
};

const HierarchyTree = ({ tree, orphans, onEdit, matchesSearch }) => {
  const [collapsed, setCollapsed] = useState(new Set());

  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="hierarchy-tree">
      {tree.length === 0 && orphans.length === 0 && (
        <p className="hierarchy-empty">No mentors found.</p>
      )}

      {tree.map((node) => (
        <TreeRow
          key={node.id}
          node={node}
          depth={0}
          onEdit={onEdit}
          collapsed={collapsed}
          onToggle={toggle}
          matchesSearch={matchesSearch}
        />
      ))}

      {orphans.length > 0 && (
        <div className="hierarchy-orphans">
          <h4 className="hierarchy-orphans-title">
            <i className="bi bi-exclamation-triangle" /> Needs attention ({orphans.length})
          </h4>
          <p className="hierarchy-orphans-hint">
            {orphans.some((o) => o.inCycle)
              ? 'These mentors have no supervisor, or are caught in a supervision loop. Assign a supervisor, or clear a link to break the loop.'
              : 'These mentors have no supervisor. Assign one to place them in the org chart.'}
          </p>
          {orphans.map((node) => (
            <TreeRow
              key={node.id}
              node={node}
              depth={0}
              onEdit={onEdit}
              collapsed={collapsed}
              onToggle={toggle}
              matchesSearch={matchesSearch}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HierarchyTree;
